import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import "katex/dist/katex.min.css";

export function formatContext(text) {
  if (!text || typeof text !== "string") return null;

  // 1️⃣ Split out GRAPH blocks first
  const graphSplit = text.split(/(\[\[GRAPH\]\][\s\S]*?\[\[\/GRAPH\]\])/g);

  return graphSplit.map((chunk, idx) => {
    // GRAPH block
    if (chunk.startsWith("[[GRAPH]]")) {
      try {
        const jsonText = chunk
          .replace("[[GRAPH]]", "")
          .replace("[[/GRAPH]]", "")
          .trim();

        const graph = JSON.parse(jsonText);
        return <GraphBlock key={`g-${idx}`} graph={graph} />;
      } catch (err) {
        console.error("❌ Failed to parse GRAPH block", err);
        return null;
      }
    }

    // Non-graph content → existing LaTeX + text logic
    return renderTextBlock(chunk, idx);
  });
}

function renderTextBlock(text, keyBase) {
  // Split block LaTeX
  const blocks = text.split(/(\\\[.*?\\\])/gs);

  return blocks.map((block, i) => {
    if (block.startsWith("\\[") && block.endsWith("\\]")) {
      return <BlockMath key={`${keyBase}-m-${i}`}>{block.slice(2, -2)}</BlockMath>;
    }

    return block
      .split(/\n+/)
      .filter(Boolean)
      .map((line, j) => (
        <p key={`${keyBase}-p-${i}-${j}`} style={{ marginBottom: 12 }}>
          {renderInline(line)}
        </p>
      ));
  });
}

function renderInline(text) {
  const mathParts = text.split(/(\\\(.*?\\\))/g);

  return mathParts.map((part, i) => {
    if (part.startsWith("\\(") && part.endsWith("\\)")) {
      return <InlineMath key={i}>{part.slice(2, -2)}</InlineMath>;
    }

    const richParts = part.split(
      /(<strong>.*?<\/strong>|<u>.*?<\/u>)/g
    );

    return richParts.map((chunk, k) => {
      if (chunk.startsWith("<strong>")) {
        return <strong key={`${i}-${k}`}>{chunk.replace(/<\/?strong>/g, "")}</strong>;
      }
      if (chunk.startsWith("<u>")) {
        return <u key={`${i}-${k}`}>{chunk.replace(/<\/?u>/g, "")}</u>;
      }
      return <React.Fragment key={`${i}-${k}`}>{chunk}</React.Fragment>;
    });
  });
}

function renderDot({ cx, cy, shape, color }) {
  const size = 6;

  switch (shape) {
    case "square":
      return <rect x={cx - size/2} y={cy - size/2} width={size} height={size} fill={color} />;
    case "triangle":
      return (
        <polygon
          points={`${cx},${cy - size} ${cx - size},${cy + size} ${cx + size},${cy + size}`}
          fill={color}
        />
      );
    case "diamond":
      return (
        <polygon
          points={`${cx},${cy - size} ${cx - size},${cy} ${cx},${cy + size} ${cx + size},${cy}`}
          fill={color}
        />
      );
    default:
      return <circle cx={cx} cy={cy} r={size / 2} fill={color} />;
  }
}

function ShapeLegend({ payload, graph }) {
  if (!payload || !graph) return null;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: 16,
        flexWrap: "wrap",
        fontSize: 13,
        marginTop: 8,
      }}
    >
      {payload.map((entry) => {
        const { value, color, dataKey } = entry;

        // 🔑 look up the series definition explicitly
        const seriesDef = graph.series.find((s) => s.key === dataKey);

        return (
          <div
            key={value}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <svg width={14} height={14}>
              {renderDot({
                cx: 7,
                cy: 7,
                shape: seriesDef?.shape,
                color,
              })}
            </svg>
            <span>{value}</span>
          </div>
        );
      })}
    </div>
  );
}

function makeTicks(min, max, step = 1) {
  const out = [];
  for (let v = min; v <= max; v += step) out.push(v);
  return out;
}

function equationToJS(equation) {
  if (!equation || typeof equation !== "string") return "NaN";

  let expr = equation.trim();

  expr = expr.replace(/^\s*y\s*=\s*/i, "");
  while (expr.includes("|")) {
    const first = expr.indexOf("|");
    const second = expr.indexOf("|", first + 1);
    if (second === -1) break;
    const inside = expr.slice(first + 1, second);
    expr = expr.slice(0, first) + `Math.abs(${inside})` + expr.slice(second + 1);
  }

  expr = expr.replace(/\^/g, "**");
  expr = expr.replace(/(\d)(x)/g, "$1*$2");
  expr = expr.replace(/(\d)\(/g, "$1*(");
  expr = expr.replace(/x\(/g, "x*(");
  expr = expr.replace(/\)\(/g, ")*(");
  expr = expr.replace(/\)(x)/g, ")*$1");

  return expr;
}

function parseEquation(equation) {
  const jsExpr = equationToJS(equation);
  const fn = new Function("x", `return (${jsExpr});`);

  return (x) => {
    try {
      const y = fn(x);
      return Number.isFinite(y) ? y : null;
    } catch {
      return null;
    }
  };
}

function sampleFunction(fn, domain, step = 0.05) {
  const data = [];
  const [xmin, xmax] = domain;

  for (let x = xmin; x <= xmax; x += step) {
    const y = fn(x);
    if (y === null) continue;
    data.push({ x: Number(x.toFixed(3)), y: Number(y.toFixed(3)) });
  }

  return data;
}

export function GraphBlock({ graph }) {
  if (!graph) return null;

  if (graph.type === "scatter") {
    const xDomain = graph.xDomain ?? [0, 10];
    const yDomain = graph.yDomain ?? [0, 10];

    const xTicks = makeTicks(xDomain[0], xDomain[1], graph.xTickStep ?? 1);
    const yTicks = makeTicks(yDomain[0], yDomain[1], graph.yTickStep ?? 1);

    const points = (graph.points ?? []).map((p) => ({
      x: p.x,
      y: p.y,
    }));

    let bestFitData = null;
    if (graph.bestFit?.equation) {
      const fn = parseEquation(graph.bestFit.equation);
      bestFitData = sampleFunction(fn, xDomain, graph.bestFit.step ?? 0.05);
    }

    return (
      <div
        onMouseDown={(e) => e.preventDefault()}
        style={{
          width: "100%",
          maxWidth: 520,
          aspectRatio: "1 / 1",
          margin: "24px auto",
          pointerEvents: "auto",
        }}
      >
        <div style={{ width: "100%", height: "100%", pointerEvents: "none" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart margin={{ top: 30, right: 30, left: 30, bottom: 30 }}>
              <CartesianGrid stroke="#999" strokeDasharray="3 3" />

              <XAxis
                type="number"
                dataKey="x"
                domain={graph.xDomain}
                ticks={xTicks}
                label={
                  graph.xLabel
                    ? { value: graph.xLabel, position: "insideBottom", offset: -20 }
                    : undefined
                }
              />

              <YAxis
                type="number"
                domain={graph.yDomain}
                ticks={yTicks}
                width={45}
                label={
                  graph.yLabel
                    ? {
                        value: graph.yLabel,
                        angle: -90,
                        position: "insideLeft",
                        offset: -10,
                        style: { textAnchor: "middle" }
                      }
                    : undefined
                }
              />

              {/* scatter points */}
              <Line
                data={points}
                dataKey="y"
                stroke="none"
                dot={{ r: 4, fill: "#000" }}
                isAnimationActive={false}
              />

              {/* optional line of best fit */}
              {bestFitData && (
                <Line
                  data={bestFitData}
                  dataKey="y"
                  stroke={graph.bestFit.style?.color ?? "#000"}
                  strokeWidth={graph.bestFit.style?.width ?? 3}
                  dot={false}
                  isAnimationActive={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (graph.type === "math") {
    const xDomain = graph.xDomain ?? [-6, 6];
    const yDomain = graph.yDomain ?? [-6, 6];

    const xTicks = graph.xTicks ?? makeTicks(xDomain[0], xDomain[1], 1);
    const yTicks = graph.yTicks ?? makeTicks(yDomain[0], yDomain[1], 1);

    const curves = (graph.curves ?? []).map((c) => {
      const fn = parseEquation(c.equation);
      const domain = c.domain ?? xDomain;

      return {
        ...c,
        data: sampleFunction(fn, domain, c.step ?? 0.05),
      };
    });

    return (
      <div
        onMouseDown={(e) => e.preventDefault()}
        style={{
          width: "100%",
          maxWidth: 520,
          aspectRatio: "1 / 1",
          margin: "24px auto",
          pointerEvents: "auto",
        }}
      >
        <div style={{ width: "100%", height: "100%", pointerEvents: "none" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart margin={{ top: 16, right: 16, left: 16, bottom: 16 }}>
              <CartesianGrid stroke="#999" strokeDasharray="3 3" />

              <XAxis
                type="number"
                dataKey="x"
                domain={xDomain}
                ticks={xTicks}
                allowDataOverflow
                axisLine={false}
                tickLine={false}
              />

              <YAxis
                type="number"
                domain={yDomain}
                ticks={yTicks}
                allowDataOverflow
                axisLine={false}
                tickLine={false}
                width={35}
              />

              <ReferenceLine x={0} stroke="#000" strokeWidth={2} />
              <ReferenceLine y={0} stroke="#000" strokeWidth={2} />

              {curves.map((c, i) => (
                <Line
                  key={i}
                  data={c.data}
                  dataKey="y"
                  stroke={c.style?.color ?? "#000"}
                  strokeWidth={c.style?.width ?? 3}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  const isLine = graph.type === "line";
  const isBar = graph.type === "bar";

  if (!isLine && !isBar) return null;

  const barData = isBar
    ? graph.groups.map((g) => ({
        label: g.label,
        ...g,
      }))
    : null;

  return (
    <div
      onMouseDown={(e) => e.preventDefault()}
      style={{
        width: "100%",
        height: 420,
        margin: "24px 0",
        pointerEvents: "auto",
      }}
    >
      <div style={{ width: "100%", height: "100%", pointerEvents: "none" }}>
        <ResponsiveContainer>
          {isLine ? (
            <LineChart
              data={graph.data}
              margin={{ top: 20, right: 30, left: 10, bottom: 40 }}
            >
              <CartesianGrid stroke="#999" strokeDasharray="3 3" />
              <XAxis dataKey={graph.xKey} />
              <YAxis
                domain={graph.yDomain}
                width={70}
                label={{
                  value: graph.yLabel,
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle" },
                }}
              />
              <Legend
                verticalAlign="bottom"
                content={(props) => (
                  <ShapeLegend {...props} graph={graph} />
                )}
              />
              {graph.series.map((s) => (
                <Line
                  key={s.key}
                  type="linear"
                  dataKey={s.key}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={(props) =>
                    renderDot({ ...props, shape: s.shape, color: s.color })
                  }
                  activeDot={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          ) : (
            <BarChart
              data={barData}
              margin={{ top: 20, right: 30, left: 10, bottom: 10 }}
            >
              <CartesianGrid stroke="#999" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                interval={0}
                angle={-35}
                textAnchor="end"
                height={80}
              />
              <YAxis
                domain={graph.yDomain}
                width={70}
                label={{
                  value: graph.yLabel,
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle" },
                }}
              />
              <Legend
                verticalAlign="bottom"
                wrapperStyle={{ paddingTop: 60 }}
                formatter={(value) =>
                  graph.series.find((s) => s.key === value)?.label ?? value
                }
              />
              {graph.series.map((s) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  fill={s.color}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}